// src/Components/PublicRoutes.js

import React, { Suspense, lazy } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { CircularProgress, Box } from "@mui/material";
import ClarityConfig from "../Integration/Clarity/ClarityConfig";
import { isCustomTrustHost } from "../Components/TrustCenter/trustCenterPillars";

// PERFORMANCE OPTIMIZATION: Lazy load public route components
// Login is small so keep it static, others can be lazy loaded

import Login from "../Pages/Login/Login"; // Keep Login static for faster initial load
const OAuthCompletePage = lazy(() => import("../Pages/Login/OAuthCompletePage"));

// Lazy load other public pages
const UserRegistration = lazy(() => import("../Pages/UserRegistration/UserRegistration"));
const UserJourney = lazy(() => import("../Pages/UserJourney/UserJourney"));
const SignUpInvitationCode = lazy(() => import("../Pages/SignupInvitationCode/SignupInvitationCode"));
const HealthCheck = lazy(() => import("../Components/Health/Health"));
const EmailReset = lazy(() => import("../Pages/EmailReset/EmailReset"));
const OtpVerification = lazy(() => import("../Pages/OtpVerification/OtpVerification"));
const ResetPassword = lazy(() => import("../Pages/ResetPassword/ResetPassword"));
const MaintenancePage = lazy(() => import("../Pages/Maintenance/MaintenancePage"));
const DocumentViewer = lazy(() => import("../Components/DocumentViewer/DocumentViewer"));
const PublicTrustCenterPage = lazy(() => import("../Pages/PublicTrustCenter/PublicTrustCenterPage"));
const PublicAssignmentPage = lazy(() => import("../Pages/PublicAssignment/PublicAssignmentPage"));
const SourceDebugger = lazy(() => import("../Components/SourceDebugger/SourceDebugger"));
const FlowTester = lazy(() => import("../Components/FlowTester/FlowTester"));

// Loading fallback for public routes
const LoadingFallback = () => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
    }}
  >
    <CircularProgress size={40} />
  </Box>
);

const PublicRoutes = () => {
  const isMaintenanceMode = process.env.REACT_APP_MAINTENANCE_MODE === 'true';

  // If in maintenance mode, show maintenance page for all routes except health check
  if (isMaintenanceMode) {
    return (
      <>
        <ClarityConfig user={null} isPublic={true} />
        <Suspense fallback={<LoadingFallback />}>
          <Routes>
            <Route path="/health" element={<HealthCheck />} />
            <Route path="*" element={<MaintenancePage />} />
          </Routes>
        </Suspense>
      </>
    );
  }

  // Normal routes when not in maintenance mode
  return (
    <>
      <ClarityConfig user={null} isPublic={true} />
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
        <Route path="/" element={isCustomTrustHost() ? <PublicTrustCenterPage byHost /> : <Login />} />
        <Route path="/login" element={<Navigate to="/" />} />
        <Route path="/login/oauth/complete" element={<OAuthCompletePage />} />
        
        {/* Registration Routes - Support source parameter for flow decisions */}
        <Route path="/register" element={<UserJourney />} />
        <Route path="/register/:source" element={<UserJourney />} />
        <Route path="/register/signup" element={<UserRegistration />} />
        <Route path="/register/signup/:source" element={<UserRegistration />} />
        <Route path="/register/invitation" element={<SignUpInvitationCode />} />
        <Route path="/register/invitation/:code" element={<SignUpInvitationCode />} />
        <Route path="/health" element={<HealthCheck />} />
        <Route path="/reset/password" element={<EmailReset />} />
        <Route path="/verify/otp" element={<OtpVerification />} />
        <Route path="/set/password" element={<ResetPassword />} />
        <Route path="/public/document-viewer" element={<DocumentViewer isPublic={true} />} />
        <Route path="/public/trust-center" element={<PublicTrustCenterPage />} />
        <Route path="/public/trust-center/:pillar" element={<PublicTrustCenterPage />} />
        <Route path="/public/assignment" element={<PublicAssignmentPage />} />
        {isCustomTrustHost() ? (
          <Route path="/:pillar" element={<PublicTrustCenterPage byHost />} />
        ) : null}
        
        {/* 🧪 Testing & Debugging - Development Only */}
        <Route path="/debug/source" element={<SourceDebugger />} />
        <Route path="/debug/source/:source" element={<SourceDebugger />} />
        <Route path="/debug/flow" element={<FlowTester />} />
        
        <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </Suspense>
    </>
  );
};

export default PublicRoutes;
